import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useMemo } from "react";

import { TrendingUp, TrendingDown } from "lucide-react";

import { CurrencyText, TranslatedText } from "@/components/common/text";
import { InfoButton } from "@/components/common/buttons";
import { ErrorComponent } from "@/components/common/errors";
import { Hr } from "@/components/common/other/Hr";
import { DoubleArrows } from "@/components/common/svgs";
import { getPredictionConfidenceTexts } from "@/utils/confidence";
import { useQuery } from "@/utils/query";
import { getRatePrediction, getRateWithModel } from "@/state/loadRatesSlice";
import { RateRangeBar } from "./RateRangeBar";
import { RateCardSkeleton } from "./RateCardSkeleton";
import { ConfidenceChip } from "./ConfidenceChip";
import { MarketRateDensityCurve } from "./MarketRateDensityCurve";
import { useRateDistributionFit } from "./useRateDistributionFit";

export const RatePrediction = () => {
    const dispatch = useDispatch();
    const { data: predictionData } = useSelector((state) => state.loadRates.ratePrediction);
    const { data: modelData, loading, error } = useSelector((state) => state.loadRates.modelRatePrediction);
    const { data: routeInfo } = useSelector((state) => state.loadRates.marketToMarketRoute);
    const { data: rateHistoryData } = useSelector((state) => state.rateHistory);
    const { pickupZip, deliveryZip, trailerType, pickupDate } = useQuery();

    useEffect(() => {
        dispatch(getRatePrediction({ trailerType, pickupZip, deliveryZip, pickupDate }));
        dispatch(getRateWithModel({ trailerType, pickupZip, deliveryZip, pickupDate }));
    }, [trailerType, pickupZip, deliveryZip, pickupDate]);

    const fit = useRateDistributionFit(modelData);

    const confidenceTexts = useMemo(() => {
        return getPredictionConfidenceTexts(modelData?.confidence);
    }, [modelData?.confidence]);

    const monthExtremes = useMemo(() => {
        const rates = (rateHistoryData?.rates || []).filter((r) => r.avgPrice != null && r.avgPrice > 0);
        if (!rates.length) return null;
        return {
            min: Math.min(...rates.map((r) => r.avgPrice)),
            max: Math.max(...rates.map((r) => r.avgPrice)),
        };
    }, [rateHistoryData]);

    // cmCertainty is already a score in [50, 100]
    const certaintyValue = modelData?.cmCertainty ?? null;
    const marketConfidence = certaintyValue != null ? Math.round(certaintyValue) : null;

    const marketRate = modelData?.averageLoadPrice?.average ?? null;
    const marketRpm = modelData?.averageRate?.average ?? null;
    // $/mi → flat price, for the hover tooltip.
    const priceScale = marketRate != null && marketRpm ? marketRate / marketRpm : null;
    const rangeMin = modelData?.averageLoadPrice?.minValue ?? monthExtremes?.min ?? null;
    const rangeMax = modelData?.averageLoadPrice?.maxValue ?? monthExtremes?.max ?? null;
    const tripCost = modelData?.estimatedTripCost;

    const emptyTrend = predictionData && predictionData.trend > -1 && predictionData.trend < 1;

    return (
        <div className="flex flex-col w-full grow">
            {loading || (!modelData && !error) ? (
                <RateCardSkeleton
                    variant="market"
                    title="Market rate"
                    infoText="Estimated flat price and RPM for the selected lane."
                />
            ) : error ? (
                <ErrorComponent />
            ) : modelData ? (
                <div className="flex flex-col w-full grow gap-5">
                  <div className="flex flex-col gap-7">
                    <div className="flex flex-col gap-2">
                        <div className="flex flex-row items-center justify-between gap-2">
                            <div className="flex flex-row items-center gap-2">
                                <TranslatedText className="text-base text-cm-p-text-primary font-medium leading-normal">
                                    Market rate
                                </TranslatedText>
                                <InfoButton text="Estimated flat price and RPM for the selected lane." />
                            </div>
                            <ConfidenceChip value={marketConfidence} />
                        </div>

                        <div className="flex flex-row items-center justify-between gap-2">
                            <div className="flex flex-row items-center gap-2">
                                <CurrencyText
                                    num={marketRate}
                                    round
                                    emptyDash
                                    className="text-cm-p-text-primary font-medium text-2xl leading-[30px]"
                                />
                                {marketRpm != null ? (
                                    <span className="text-cm-p-text-secondary text-sm font-normal leading-normal">
                                        {marketRpm.toFixed(2)}/mi
                                    </span>
                                ) : null}
                            </div>
                            {predictionData ? (
                                <div
                                    className={`shrink-0 text-xs/[19px] flex flex-row items-center ${
                                        emptyTrend
                                            ? "text-cm-p-text-secondary"
                                            : predictionData.trend > 0
                                                ? "text-cm-p-accent-800"
                                                : "text-cm-p-on-container-red"
                                    }`}
                                >
                                    {emptyTrend ? (
                                        <div className="mr-1">
                                            <DoubleArrows />
                                        </div>
                                    ) : predictionData.trend > 0 ? (
                                        <TrendingUp className="mr-1 h-4 w-4" />
                                    ) : (
                                        <TrendingDown className="mr-1 h-4 w-4" />
                                    )}
                                    <div className="whitespace-nowrap">
                                        {emptyTrend ? (
                                            <TranslatedText>no change </TranslatedText>
                                        ) : (
                                            <>
                                                <span className="font-medium">{Math.round(predictionData.trend)}</span>
                                                <span>% </span>
                                            </>
                                        )}
                                        <TranslatedText>in next days</TranslatedText>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    {fit ? (
                        <MarketRateDensityCurve
                            fit={fit}
                            markerRpm={marketRpm}
                            priceScale={priceScale}
                            minPrice={rangeMin}
                            maxPrice={rangeMax}
                        />
                    ) : (
                        <RateRangeBar leftLabel="Min" rightLabel="Max" minValue={rangeMin} maxValue={rangeMax} markerValue={marketRate} />
                    )}
                  </div>

                    <div className="flex flex-col grow gap-3">
                        <Hr className="my-0" />

                        <div className="flex flex-col gap-1 text-sm text-cm-p-text-primary">
                            <TranslatedText className="uppercase text-xs/[19px] text-cm-p-text-secondary font-medium">
                                Lane stats
                            </TranslatedText>
                            <div className="flex flex-row flex-wrap items-center gap-x-2 gap-y-1">
                                {routeInfo ? (
                                    <span>
                                        {Math.round(routeInfo.distance)} <TranslatedText>mi</TranslatedText>
                                    </span>
                                ) : null}
                                {routeInfo && (tripCost?.fuelCost || tripCost?.tollCost) ? (
                                    <span className="text-cm-p-text-primary">&middot;</span>
                                ) : null}
                                <span>
                                    {tripCost?.fuelCost ? (
                                        <>
                                            <CurrencyText
                                                className="text-sm text-cm-p-text-primary font-normal"
                                                num={tripCost?.fuelCost}
                                                round
                                            />{" "}
                                            <TranslatedText>fuel</TranslatedText>
                                        </>
                                    ) : null}
                                    {tripCost?.tollCost ? (
                                        <>
                                            {tripCost?.fuelCost ? " + " : null}
                                            <CurrencyText
                                                className="text-sm text-cm-p-text-primary font-normal"
                                                num={tripCost?.tollCost}
                                                round
                                            />{" "}
                                            <TranslatedText>tolls</TranslatedText>
                                        </>
                                    ) : null}
                                    {!tripCost?.fuelCost && !tripCost?.tollCost ? (
                                        <TranslatedText>No fuel or toll data</TranslatedText>
                                    ) : null}
                                </span>
                            </div>
                        </div>

                        <Hr className="my-0" />

                        <div className="flex flex-col gap-1">
                            <div className="flex flex-row items-center gap-2">
                                <TranslatedText className="uppercase text-xs/[19px] text-cm-p-text-secondary font-medium">
                                    {confidenceTexts.title}
                                </TranslatedText>
                                <InfoButton text="The level volume of the load reflects how often it's driven and how easily rates can be predicted." />
                            </div>
                            <TranslatedText className="text-sm text-cm-p-text-primary leading-[18px]">
                                {confidenceTexts.description}
                            </TranslatedText>
                        </div>

                    </div>
                </div>
            ) : null}
        </div>
    );
};
